import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Form, Formik} from "formik";
import {Grid} from "@material-ui/core";
import {PublisherService} from "../../../services/publisher.service";
import {useDispatch, useSelector} from "react-redux";
import {toggleLoader} from "../../../store/global/global-slice";
import {RootState} from "../../../store/root-reducer";
import {StyledButton} from "../../ui-base/button";
import * as Yup from "yup";
import {logged} from "../../../store/auth/auth-slice";
import {AuthService} from "../../../services/auth.service";
import {SuccessModal} from "../../ui-base/success-modal";
import {useTranslation} from "react-i18next";
import {EditProfileFormStep1} from "./edit-profile-form-step1";
import {EditProfileFormStep2} from "./edit-profile-form-step2";
import {EditProfileFormStep3} from "./edit-profile-form-step3";
import {EditProfileFormStep4} from "./edit-profile-form-step4";
import {EditProfileFormStep5} from "./edit-profile-form-step5";
import {EditProfileForm} from "../../../models/forms/publisher/edit-profile-form.model";
import {CountryModel} from "../../../models/country.model";
import {AllLanguages} from "../../../data/languages";
import {CategoryModel} from "../../../models/category.model";
import {CategoryCsvModel} from "../../../models/category-csv.model";
import {BaseService} from "../../../services/base.service";

const EditFormSchema = Yup.object().shape({
	name: Yup.string().required(),
	nip: Yup.string().required(),
	country: Yup.mixed().required(),
	mainCategory: Yup.string().required(),
	categories: Yup.array().min(1).required(),
	publisherType: Yup.boolean(),
	agencyType: Yup.boolean()
}).test(
	'accountTypeTest',
	null,
	(obj) => {
		if ( obj.publisherType || obj.agencyType ) {
			return true;
		}

		return new Yup.ValidationError(
			'Please check one checkbox',
			null,
			'publisherType'
		);
	}
);

const EditPublisherProfileForm = () => {
	const [activeStep, setActiveStep] = React.useState(1);
	const [file, setFile] = useState(null);
	const [profile, setProfile] = useState(null);
	const [initialPhoto, setInitialPhoto] = useState(null);
	const [infoMessage, setInfoMessage] = useState(false);
	const {t} = useTranslation(['publisher', 'common']);
	const dispatch = useDispatch();

	const themaCategories: CategoryModel[] = useSelector(
		(state: RootState) => state.categories.categories
	);

	const categoriesCsv: CategoryCsvModel[] = useSelector(
		(state: RootState) => state.categories.categoriesCsv
	);

	const countries: CountryModel[] = useSelector(
		(state: RootState) => state.countries.countries
	);

	const handleFile = (files: File[]) => {
		setFile(files[0]);
	}

	const formInitialValues = useMemo(() => ({
		name: '',
		contactPerson: '',
		phone: '',
		phonePrefix: '',
		contactEmail: '',
		address: '',
		nip: '',
		postalCode: '',
		city: '',
		country: null,
		publishingLanguageBooks: [],
		links: [],
		textService1: false,
		textService2: false,
		textService3: false,
		textService4: false,
		graphicService1: false,
		graphicService2: false,
		graphicService3: false,
		otherService1: false,
		otherService2: false,
		otherService3: false,
		otherService4: false,
		otherService4text: '',
		numberOfBooks: '',
		averageNumberOfBooks: '',
		description: '',
		description2: '',
		mainCategory: '',
		categories: [],
		publisherType: true,
		agencyType: false
	}), []);

	const formSubmit = async (values: EditProfileForm) => {
		if (activeStep === 5) {
			dispatch(toggleLoader(true));

			const res = await PublisherService.updateProfile(values, profile.id);

			if (res.status) {
				if (file) {
					await PublisherService.updateLogo(file, res.data.id);
					setProfile({...profile, id: res.data.id});
					dispatch(toggleLoader(false));
				} else {
					setProfile({...profile, id: res.data.id});
					dispatch(toggleLoader(false));
				}

				AuthService.authMe().then((res) => {
					if (res) {
						dispatch(logged(res));
						setInfoMessage(true);
					}
				});
			} else {
				dispatch(toggleLoader(false));
			}
		} else {
			goToNextStep();
		}
	}

	const loadProfile = useCallback(async () => {
		dispatch(toggleLoader(true));
		const profile = await PublisherService.getMe();

		if (profile) {
			const filteredCategories: string[] = categoriesCsv.filter((category) => category[BaseService.parseOldMainCategory(profile.main_category)] === 'TRUE' && category['Do wyrzucenia'] === 'FALSE').map((category) => category.Code);

			formInitialValues.name = profile.name || '';
			formInitialValues.contactPerson = profile.details?.contact_person || '';
			formInitialValues.phone = profile.details?.phone || '';
			formInitialValues.phonePrefix = profile.details?.phone_prefix || '';
			formInitialValues.contactEmail = profile.details?.contact_email || '';
			formInitialValues.address = profile.details?.address || '';
			formInitialValues.nip = profile.details?.nip || '';
			formInitialValues.postalCode = profile.details?.postal_code || '';
			formInitialValues.city = profile.details?.city || '';
			formInitialValues.country = profile.details?.country ? countries.find((country) => country.iso === profile.details?.country) : null;
			formInitialValues.publishingLanguageBooks = profile.book_languages ? AllLanguages.filter((lang) => {
				return profile.book_languages.includes(lang.code);
			}) : [];
			formInitialValues.links = profile.external_links || [];
			formInitialValues.textService1 = profile.looking_for_editing || false;
			formInitialValues.textService2 = profile.looking_for_translation || false;
			formInitialValues.textService3 = profile.looking_for_reviewing || false;
			formInitialValues.textService4 = profile.looking_for_ghost_writing || false;
			formInitialValues.graphicService1 = profile.looking_for_illustration || false;
			formInitialValues.graphicService2 = profile.looking_for_covers || false;
			formInitialValues.graphicService3 = profile.looking_for_composition || false;
			formInitialValues.otherService1 = profile.looking_for_funding || false;
			formInitialValues.otherService2 = profile.looking_for_bookmarketing || false;
			formInitialValues.otherService3 = profile.looking_for_literary_agent || false;
			formInitialValues.otherService4 = !!(profile.looking_for_other) || false;
			formInitialValues.otherService4text = profile.looking_for_other || '';
			formInitialValues.numberOfBooks = profile.annual_number_of_titles?.toString() || '';
			formInitialValues.averageNumberOfBooks = profile.average_edition_size?.toString() || '';
			formInitialValues.description = profile.description || '';
			formInitialValues.description2 = profile.looking_for_description || '';
			formInitialValues.mainCategory = BaseService.parseOldMainCategory(profile.main_category || '');
			formInitialValues.publisherType = true;
			formInitialValues.agencyType = profile.is_an_agency || false;
			formInitialValues.categories = profile.categories_wanted ?
				profile.categories_wanted.map((category) => {
					const foundCategory = themaCategories.find((cat) => cat.code === category.code);

					const children = themaCategories.filter((category) => filteredCategories.includes(category.code) && category.parent_code === foundCategory.code);

					if (children && children.length > 0) {
						return {
							...foundCategory,
							id: foundCategory.id,
							name: t('categoriesComponent.general'),
							description: t('categoriesComponent.general'),
							children: [],
							code: foundCategory.code + '_GENERALCATEGORY',
							parent_code: foundCategory.code
						}
					} else {
						return foundCategory;
					}
				})
				: [];

			if (profile.logo && profile.logo.file) {
				setInitialPhoto(profile.logo.file);
			}

			setProfile(profile);

			dispatch(toggleLoader(false));
		}
	}, [dispatch, countries, formInitialValues, t, themaCategories, categoriesCsv]);

	useEffect(() => {
		dispatch(toggleLoader(true));
		loadProfile();
	}, [dispatch, loadProfile]);

	const goToNextStep = () => {
		setActiveStep(activeStep + 1);
	}

	const goToPreviousStep = () => {
		setActiveStep(activeStep - 1);
	}

	return (
		<>
			<Formik
				initialValues={formInitialValues}
				onSubmit={(values: any) => {
					formSubmit(values);
				}}
				validationSchema={EditFormSchema}
			>
				{({values, touched, errors, handleChange, handleBlur, isValid, setFieldValue}) => (
					<Form>
						<Grid container direction="row" justifyContent="flex-end" alignItems={"center"} spacing={2}>
							{activeStep > 1 &&
                                <>
                                    <Grid item>
                                        <StyledButton type="button" color={'secondary'} onClick={goToPreviousStep}>
											{'< ' + t('common:back')}
                                        </StyledButton>
                                    </Grid>
                                </>
							}

							{activeStep < 5 && activeStep > 1 &&
                                <>
                                    <Grid item>
                                        <StyledButton type="button" color={'violet'} onClick={goToNextStep}>
											{t('common:next') + ' >'}
                                        </StyledButton>
                                    </Grid>
                                </>
							}

							{activeStep === 1 &&
                                <>
                                    <Grid item>
                                        <StyledButton type="submit" color={'violet'}>{t('common:next') + ' >'}</StyledButton>
                                    </Grid>
                                </>
							}

							{activeStep === 5 &&
                                <>
                                    <Grid item>
                                        <StyledButton type="submit" color={'violet'}>{t('common:save')}</StyledButton>
                                    </Grid>
                                </>
							}
						</Grid>

						{activeStep === 1 && <EditProfileFormStep1
                            initialPhoto={initialPhoto}
                            handleFile={handleFile}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            errors={errors}
                            touched={touched}
                        />}

						{activeStep === 2 && <EditProfileFormStep2
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            errors={errors}
                            touched={touched}
                        />}

						{activeStep === 3 && <EditProfileFormStep3
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            errors={errors}
                            touched={touched}
                        />}

						{activeStep === 4 && <EditProfileFormStep4
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            errors={errors}
                            touched={touched}
                        />}

						{activeStep === 5 && <EditProfileFormStep5
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            errors={errors}
                            touched={touched}
                        />}
					</Form>
				)}
			</Formik>
			<SuccessModal open={infoMessage} setOpen={setInfoMessage} title={t('editProfile.successTitle')}
						  buttonText={t('editProfile.successButton')} buttonLink={`/publisher/profile/${profile?.id}`}>
				<p>{t('editProfile.successDesc1')}</p>
				<p style={{marginTop: '10px'}}>{t('editProfile.successDesc2')}</p>
				<p>{t('editProfile.successDesc3')}</p>
				<p>{t('editProfile.successDesc4')}</p>
			</SuccessModal>
		</>
	)
}

export default EditPublisherProfileForm;
